import { useCallback, useEffect, useRef, useState } from 'react';

import { analytics } from '@/services/analytics';
import { fetchLibraryBooks } from '@/services/booksApi';
import { Book } from '@/utils/bookRouting';

const SEARCH_DEBOUNCE_MS = 250;
const RESULT_LIMIT = 6;

interface HeaderSearchResults {
  results: Book[];
  total: number;
  /** The query the current results answer, or '' before any have loaded. */
  resultsQuery: string;
  isLoading: boolean;
  error: string | null;
  /** Search for the current query now instead of waiting out the debounce. */
  flush: () => void;
}

/** Live library search results for the top navigation's search field. */
export function useHeaderSearchResults(query: string, enabled: boolean): HeaderSearchResults {
  const trimmedQuery = enabled ? query.trim() : '';
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery);
  const [results, setResults] = useState<Book[]>([]);
  const [total, setTotal] = useState(0);
  const [resultsQuery, setResultsQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastTrackedQueryRef = useRef('');

  useEffect(() => {
    // Clearing the field should clear the panel at once, not after the delay.
    if (!trimmedQuery) {
      setDebouncedQuery('');
      return;
    }

    const timer = window.setTimeout(() => setDebouncedQuery(trimmedQuery), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmedQuery]);

  useEffect(() => {
    if (!debouncedQuery) {
      setResults([]);
      setTotal(0);
      setResultsQuery('');
      setIsLoading(false);
      setError(null);
      return;
    }

    const abortController = new AbortController();
    let ignore = false;

    const loadResults = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await fetchLibraryBooks(
          { q: debouncedQuery, page: 1, pageSize: RESULT_LIMIT },
          abortController.signal,
        );

        if (ignore || abortController.signal.aborted) return;

        if (debouncedQuery !== lastTrackedQueryRef.current) {
          lastTrackedQueryRef.current = debouncedQuery;
          analytics.track('Library Search', {
            query: debouncedQuery,
            result_count: data.total,
            surface: 'top_nav',
            trigger: 'automatic',
            input_method: 'automatic',
          });
        }

        setResults(data.items);
        setTotal(data.total);
        setResultsQuery(debouncedQuery);
      } catch (loadError) {
        if (ignore || abortController.signal.aborted) return;

        console.error('Failed to load header search results:', loadError);
        analytics.trackErrorShown('top_nav_search', loadError, {
          surface: 'top_nav',
          operation: 'library_search',
        });
        setError('Search is unavailable right now.');
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    loadResults();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [debouncedQuery]);

  const flush = useCallback(() => {
    setDebouncedQuery(trimmedQuery);
  }, [trimmedQuery]);

  return { results, total, resultsQuery, isLoading, error, flush };
}
